import React from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, Globe, Users, Eye, Heart, Zap } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import WhatsAppFloat from "../WhatsAppFloat";
import PageMeta from "../PageMeta";

export default function About() {
  const { lang } = useParams();
  const { t } = useTranslation("about");
  const prefix = lang ? `/${lang}` : "";

  const values = [
    { icon: Eye, gradient: "from-blue-500 to-blue-600", key: "transparency" },
    { icon: Shield, gradient: "from-green-500 to-emerald-600", key: "privacy" },
    { icon: Zap, gradient: "from-purple-500 to-violet-600", key: "simplicity" },
    { icon: Heart, gradient: "from-pink-500 to-rose-600", key: "trust" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title={`${t("hero.title")} — TrackTeam`}
        description={t("hero.subtitle")}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-br from-slate-50 via-blue-50/40 to-purple-50/30 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }}></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
            {t("hero.title")}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t("hero.subtitle")}
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t("mission.title")}</h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">{t("mission.text")}</p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gradient-to-br from-[#62329f] via-purple-700 to-indigo-800 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-extrabold mb-1">10,000+</div>
              <div className="text-purple-200 text-sm">{t("stats.teams")}</div>
            </div>
            <div>
              <div className="text-4xl font-extrabold mb-1">50+</div>
              <div className="text-purple-200 text-sm">{t("stats.countries")}</div>
            </div>
            <div>
              <div className="text-4xl font-extrabold mb-1">10</div>
              <div className="text-purple-200 text-sm">{t("stats.languages")}</div>
            </div>
            <div>
              <div className="text-4xl font-extrabold mb-1">4.5</div>
              <div className="text-purple-200 text-sm">{t("stats.rating")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">{t("values.title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((val) => {
              const VIcon = val.icon;
              return (
                <div key={val.key} className="bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-lg transition-shadow">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${val.gradient} flex items-center justify-center mb-6`}>
                    <VIcon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t(`values.${val.key}.title`)}</h3>
                  <p className="text-gray-600 leading-relaxed">{t(`values.${val.key}.text`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t("company.title")}</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">{t("company.text")}</p>
          <div className="flex justify-center gap-4">
            <Link
              to={`${prefix}/contact`}
              className="inline-flex items-center gap-2 bg-[#62329f] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#4f267f] transition-colors"
            >
              {t("company.contactCta")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
